import { folderTypes } from "@redotech/redo-model/views/folders";
import {
  ClaimView,
  RecycleView,
  ReturnView,
  WarrantyView,
} from "@redotech/redo-model/views/views";
import { useMemo } from "react";
import { FolderWithView } from "./view-to-tree-utils";

type View = ReturnView | WarrantyView | ClaimView | RecycleView;
type ViewType = "returns" | "warranties" | "claims" | "recycle";

interface UseViewFolderLogicParams<T extends ViewType> {
  allViews: View[];
  allFolders: folderTypes["returns"][];
  notificationCounts: Record<string, number>;
}

interface UseViewFolderLogicResult<T extends ViewType> {
  visibleViews: View[];
  viewsWithoutFolders: View[];
  foldersWithViews: FolderWithView<T>[];
  folderNotificationCounts: Record<string, number>;
  totalNotificationCount: number;
}

export function useViewFolderLogic<T extends ViewType>({
  allViews,
  allFolders,
  notificationCounts,
}: UseViewFolderLogicParams<T>): UseViewFolderLogicResult<T> {
  const visibleViews = useMemo(() => {
    return allViews.filter((view) => {
      if (view.hideWhenEmpty) {
        const count = notificationCounts[view._id] || 0;
        return count > 0;
      }
      return true;
    });
  }, [allViews, notificationCounts]);

  const viewsWithoutFolders = useMemo(() => {
    return visibleViews.filter((view) => !view.folderId);
  }, [visibleViews]);

  const foldersWithViews = useMemo<FolderWithView<T>[]>(() => {
    return allFolders.map((folder) => {
      return {
        folder,
        views: visibleViews.filter((view) => view.folderId === folder._id),
      };
    });
  }, [allFolders, visibleViews]);

  const folderNotificationCounts = useMemo(() => {
    const folderCounts: Record<string, number> = {};

    for (const { folder, views } of foldersWithViews) {
      const totalCount = views.reduce((sum: number, view) => {
        return sum + (notificationCounts[view._id] || 0);
      }, 0);

      if (totalCount > 0) {
        folderCounts[folder._id] = totalCount;
      }
    }

    return folderCounts;
  }, [foldersWithViews, notificationCounts]);

  const totalNotificationCount = useMemo(() => {
    return visibleViews.reduce((sum: number, view) => {
      return sum + (notificationCounts[view._id] || 0);
    }, 0);
  }, [visibleViews, notificationCounts]);

  return {
    visibleViews,
    viewsWithoutFolders,
    foldersWithViews,
    folderNotificationCounts,
    totalNotificationCount,
  };
}
